// components/Modals/ContentModal/tabs/MediaTab.js
import React, { useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import API_URL from '../../../../config';

function MediaTab({
    mediaPreviews,
    setMediaPreviews,
    mediaFiles,
    setMediaFiles,
    contentForm,
    setContentForm,
    selectedMediaIndex,
    setSelectedMediaIndex,
    handleOpenWatermark,
    handleOpenVideoWatermark,
    isEditMode = false,
    existingMedia = []
}) {
    const fileInputRef = useRef(null);

    // Düzenleme modunda mevcut medyaları yükle
    useEffect(() => {
        if (isEditMode && existingMedia && existingMedia.length > 0 && mediaPreviews.length === 0) {
            console.log('📸 Mevcut medyalar yükleniyor:', existingMedia);

            const loadedPreviews = existingMedia.map((media, index) => {
                // Obje mi string mi kontrol et
                let mediaUrl, mediaType;

                if (typeof media === 'string') {
                    // String ise eski format
                    mediaUrl = media;
                    mediaType = media.includes('.mp4') || media.includes('.mov') || media.includes('.avi') ? 'video' : 'image';
                } else if (typeof media === 'object' && media.url) {
                    // Obje ise yeni format
                    mediaUrl = media.url;
                    mediaType = media.type;
                } else {
                    console.warn('Bilinmeyen medya formatı:', media);
                    return null;
                }

                // URL formatını düzelt
                const fullUrl = mediaUrl.startsWith('http')
                    ? mediaUrl
                    : `${API_URL}/${mediaUrl}`;

                return {
                    url: fullUrl,
                    type: mediaType,
                    name: mediaUrl.split('/').pop(),
                    size: 0,
                    isExisting: true,
                    file: null
                };
            }).filter(Boolean); // null olanları temizle

            setMediaPreviews(loadedPreviews);
            console.log('✅ Medyalar yüklendi:', loadedPreviews.length);
        }
    }, [isEditMode, existingMedia, mediaPreviews.length, setMediaPreviews]);

    const handleMediaSelect = (e) => {
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        // Toplam medya sayısını kontrol et (mevcut + yeni)
        const totalMediaCount = mediaPreviews.length + files.length;
        if (totalMediaCount > 20) {
            toast.error('Maksimum 20 medya dosyası yükleyebilirsiniz');
            e.target.value = null;
            return;
        }

        const validFiles = [];
        const newPreviews = [];

        files.forEach(file => {
            if (file.size > 200 * 1024 * 1024) {
                toast.error(`${file.name} dosyası 200MB'dan büyük`);
                return;
            }

            if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                toast.error(`${file.name} geçersiz dosya formatı`);
                return;
            }

            validFiles.push(file);
        });

        validFiles.forEach(file => {
            if (file.type.startsWith('video/')) {
                const videoUrl = URL.createObjectURL(file);
                newPreviews.push({
                    url: videoUrl,
                    type: 'video',
                    name: file.name,
                    size: file.size,
                    isExisting: false,
                    file: file
                });
            } else {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setMediaPreviews(prev => [...prev, {
                        url: reader.result,
                        type: 'image',
                        name: file.name,
                        size: file.size,
                        isExisting: false,
                        file: file
                    }]);
                };
                reader.readAsDataURL(file);
            }
        });

        if (newPreviews.length > 0) {
            setMediaPreviews(prev => [...prev, ...newPreviews]);
        }

        setMediaFiles(prev => [...prev, ...validFiles]);
        setContentForm({ ...contentForm, media: [...mediaFiles, ...validFiles] });

        e.target.value = null;
    };

    const handleMediaRemove = (indexToRemove) => {
        if (indexToRemove === undefined) {
            // Tümünü temizle
            mediaPreviews.forEach(preview => {
                if (preview.type === 'video' && preview.url.startsWith('blob:')) {
                    URL.revokeObjectURL(preview.url);
                }
            });

            setMediaFiles([]);
            setMediaPreviews([]);
            setContentForm({ ...contentForm, media: null, keepExistingMedia: false });
            setSelectedMediaIndex(0);

            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }

            toast.success('Tüm medyalar temizlendi');
            return;
        }

        const preview = mediaPreviews[indexToRemove];

        // Blob URL'i temizle
        if (preview && preview.type === 'video' && preview.url.startsWith('blob:')) {
            URL.revokeObjectURL(preview.url);
        }

        const newMediaPreviews = mediaPreviews.filter((_, index) => index !== indexToRemove);

        // Sadece yeni yüklenen dosyaları filtrele (mevcut olanları çıkar)
        const newMediaFiles = mediaFiles.filter((_, index) => {
            const previewIndex = mediaPreviews.findIndex(p => !p.isExisting);
            return index !== (indexToRemove - previewIndex);
        });

        setMediaFiles(newMediaFiles);
        setMediaPreviews(newMediaPreviews);
        setContentForm({
            ...contentForm,
            media: newMediaFiles,
            removedMediaIndexes: [...(contentForm.removedMediaIndexes || []), indexToRemove]
        });

        if (selectedMediaIndex >= newMediaPreviews.length && newMediaPreviews.length > 0) {
            setSelectedMediaIndex(newMediaPreviews.length - 1);
        } else if (newMediaPreviews.length === 0) {
            setSelectedMediaIndex(0);
        }

        toast.success('Medya kaldırıldı');
    };

    return (
        <div className="tab-content animate-fadeIn">
            <div className="media-upload-area">
                {mediaPreviews.length === 0 ? (
                    <div className="media-dropzone">
                        <div className="dropzone-icon">📁</div>
                        <h3>Medya Yükle</h3>
                        <p>Birden fazla resim veya video seçebilirsiniz</p>

                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*,video/*"
                            onChange={handleMediaSelect}
                            multiple
                            style={{ display: 'none' }}
                            id="media-upload"
                        />

                        <label htmlFor="media-upload" className="btn-upload">
                            <span>📎</span> Dosya Seç
                        </label>

                        <div className="upload-hint">
                            JPG, PNG, MP4 • Maksimum 200MB • En fazla 20 dosya
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="media-preview-container">
                            <div className="media-preview-header">
                                <h4>Seçili Medya</h4>
                                <div className="media-preview-actions">
                                    {mediaPreviews[selectedMediaIndex]?.type === 'image' && (
                                        <button
                                            type="button"
                                            onClick={() => handleOpenWatermark(selectedMediaIndex)}
                                            className="btn-watermark"
                                            title="Icon/Logo Ekle"
                                        >
                                            <span>💧</span> Icon/Logo
                                        </button>
                                    )}
                                    {mediaPreviews[selectedMediaIndex]?.type === 'video' && (
                                        <button
                                            type="button"
                                            onClick={() => handleOpenVideoWatermark(selectedMediaIndex)}
                                            className="btn-watermark btn-video-watermark"
                                            title="Video Logo Ekle"
                                        >
                                            <span>🎬</span> Logo Ekle
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleMediaRemove(selectedMediaIndex)}
                                        className="btn-remove-single"
                                        title="Kaldır"
                                    >
                                        <span>✕</span>
                                    </button>
                                </div>
                            </div>

                            <div className="media-preview-content">
                                {mediaPreviews[selectedMediaIndex] && (
                                    <>
                                        {mediaPreviews[selectedMediaIndex].type === 'image' ? (
                                            <img
                                                src={mediaPreviews[selectedMediaIndex].url}
                                                alt="Önizleme"
                                                className="preview-image"
                                            />
                                        ) : (
                                            <video
                                                src={mediaPreviews[selectedMediaIndex].url}
                                                controls
                                                className="preview-video"
                                            />
                                        )}

                                        <div className="media-info-bar">
                                            <span className="media-filename">
                                                {mediaPreviews[selectedMediaIndex].type === 'video' ? '📹' : '🖼️'}
                                                {mediaPreviews[selectedMediaIndex].name}
                                            </span>
                                            {mediaPreviews[selectedMediaIndex].size > 0 && (
                                                <span className="media-filesize">
                                                    {(mediaPreviews[selectedMediaIndex].size / (1024 * 1024)).toFixed(2)} MB
                                                </span>
                                            )}
                                            {mediaPreviews[selectedMediaIndex].isExisting && (
                                                <span className="existing-badge">📌 Mevcut</span>
                                            )}
                                            {mediaPreviews[selectedMediaIndex].watermarked && (
                                                <span className="watermark-badge">💧 Icon/Logo</span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="media-thumbnails-container">
                            <div className="thumbnails-header">
                                <h4>Yüklenen Medyalar ({mediaPreviews.length}/20)</h4>
                                <div className="thumbnails-actions">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*,video/*"
                                        onChange={handleMediaSelect}
                                        multiple
                                        style={{ display: 'none' }}
                                        id="media-add-more"
                                    />
                                    <label htmlFor="media-add-more" className="btn-add-more">
                                        <span>➕</span> Ekle
                                    </label>

                                    <button
                                        type="button"
                                        onClick={() => handleMediaRemove()}
                                        className="btn-clear-all"
                                    >
                                        <span>🗑️</span> Tümünü Temizle
                                    </button>
                                </div>
                            </div>

                            <div className="media-thumbnails">
                                {mediaPreviews.map((preview, index) => (
                                    <div
                                        key={index}
                                        className={`thumbnail-item ${selectedMediaIndex === index ? 'selected' : ''} ${preview.isExisting ? 'existing-media' : ''}`}
                                        onClick={() => setSelectedMediaIndex(index)}
                                    >
                                        {preview.type === 'image' ? (
                                            <img
                                                src={preview.url}
                                                alt={`Thumbnail ${index + 1}`}
                                                className="thumbnail-image"
                                            />
                                        ) : (
                                            <div className="thumbnail-video">
                                                <span className="video-icon">📹</span>
                                            </div>
                                        )}

                                        {preview.isExisting && (
                                            <div className="thumbnail-existing-badge">📌</div>
                                        )}

                                        {preview.watermarked && (
                                            <div className="thumbnail-watermark-badge">💧</div>
                                        )}

                                        <div className="thumbnail-number">
                                            {index + 1}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default MediaTab;